import {FC, ReactElement} from 'react'
import classNames from 'classnames'
import {SliceStatus} from 'interfaces'
import {ColumnSkeleton, EmptyTdElement} from './ColumnSkeleton'
import {useResizeDetector} from 'react-resize-detector'
import {Row} from '../Table/Table.styles'
import {useTranslation} from 'react-i18next'
import {QueryStatus} from '@tanstack/react-query'
import {defaultResizeHookOptions} from 'definitions/constant'

interface Column {
  name: string
  dataKey: string
  widthPercentage: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderColumn?: (props: any) => ReactElement
}

type Props = {
  columns: Column[]
  numberOfRows?: number
  noData: string
  status: SliceStatus | QueryStatus
  alignIndexRight?: number[]
  filterIcon?: React.ReactNode
  filterSelected?: boolean
}

interface TableBodySkeletonProps {
  numberOfRows: number
  columns: Column[]
  alignIndexRight: number[]
}

export const TableSkeleton: FC<Props> = ({
  columns,
  numberOfRows = 5,
  status,
  noData,
  alignIndexRight = [],
  filterIcon,
  filterSelected,
}) => {
  const {width, ref} = useResizeDetector(defaultResizeHookOptions)

  const {t} = useTranslation(['common'])

  const exactWidthCalc = (() =>
    width ? columns.map(col => (col.widthPercentage * width) / 100) : [])()
  return (
    <table
      aria-label="Skeleton Table"
      aria-busy={status === 'loading'}
      className="w-full table-fixed"
      ref={ref}
    >
      <thead>
        <Row className="text-dope-th text-xs karla font-thin text-left uppercase ">
          <th className="tableTdThWidth"></th>
          {columns.map((d, idx) => {
            const {renderColumn, ...rest} = d
            return (
              <th
                className={classNames(
                  'pr-4 py-4 border-b-0.5 border-gray-600',
                  {
                    'text-right': alignIndexRight.includes(idx),
                  },
                )}
                key={idx}
                style={{width: `${d.widthPercentage}%`}}
              >
                {!renderColumn ? (
                  <div
                    className={classNames({
                      'pr-4 flex': !alignIndexRight.includes(idx),
                    })}
                  >
                    <span
                      className={classNames({
                        'text-blue-400':
                          d.dataKey === 'status' && filterSelected,
                        'ml-2.5': d.dataKey === 'updatedOn',
                      })}
                    >
                      {d.name}
                    </span>
                    {d.dataKey === 'status' ? filterIcon : null}
                  </div>
                ) : (
                  renderColumn(rest)
                )}
              </th>
            )
          })}
          <th className="tableTdThWidth"></th>
        </Row>
      </thead>
      <tbody>
        {status === SliceStatus.pending || status === 'loading' ? (
          Array(numberOfRows)
            .fill({})
            .map((_, idx) => {
              return (
                <Row
                  data-cy="table-row-fb"
                  key={`table-skeleton-row${idx}`}
                  className="text-gray-100 text-xs karla"
                  aria-label="Skeleton Table Row"
                >
                  <EmptyTdElement />
                  {Array(columns.length)
                    .fill({})
                    .map((__, index) => (
                      <td
                        className={classNames(
                          'pr-4 py-4 border-b-0.5 border-gray-600',
                          {
                            'text-right': alignIndexRight.includes(index),
                          },
                        )}
                        key={index}
                      >
                        <ColumnSkeleton
                          classes={classNames('absolute', {
                            'float-right': alignIndexRight.includes(index),
                          })}
                          width={exactWidthCalc[index]}
                        />
                      </td>
                    ))}
                  <EmptyTdElement />
                </Row>
              )
            })
        ) : (
          <Row
            className="text-xs text-gray-100 border-gray-600"
            data-cy="No data"
          >
            <EmptyTdElement />
            <td className="w-full py-3" colSpan={columns.length}>
              {status === SliceStatus.resolved || status === 'success'
                ? noData
                : t('SOMETHING_WENT_WRONG')}
            </td>
            <EmptyTdElement />
          </Row>
        )}
      </tbody>
    </table>
  )
}

export const TableBodySkeleton = ({
  numberOfRows,
  columns,
  alignIndexRight,
}: TableBodySkeletonProps) => {
  return (
    <tbody className="w-full">
      {Array(numberOfRows)
        .fill({})
        .map((_, idx) => {
          return (
            <Row
              data-cy="table-single-row-skeleton"
              key={`table-skeleton-row-${idx}`}
              className="text-gray-100 text-xs karla"
              aria-label="Skeleton Table Row"
            >
              <EmptyTdElement />
              {Array(columns.length)
                .fill({})
                .map((__, index) => (
                  <td
                    className={classNames(
                      'pr-4 py-4 border-b-0.5 border-gray-600',
                      {
                        'text-right': alignIndexRight.includes(index),
                      },
                    )}
                    key={`skeleton-column-${columns[index].name}`}
                  >
                    <ColumnSkeleton
                      classes={classNames('absolute', {
                        'float-right': alignIndexRight.includes(index),
                      })}
                      width={index === 0 ? 50 : 100}
                    />
                  </td>
                ))}
              <EmptyTdElement />
            </Row>
          )
        })}
    </tbody>
  )
}
